let googleMapsScriptPromise: Promise<void> | null = null;
let runtimeConfigPromise: Promise<void> | null = null;

const getGoogleMapsApiKey = () =>
  process.env.REACT_APP_GOOGLE_MAPS_API_KEY ||
  (window as typeof window & { __APP_CONFIG__?: Record<string, string> }).__APP_CONFIG__?.REACT_APP_GOOGLE_MAPS_API_KEY ||
  (window as typeof window & { __ENV__?: Record<string, string> }).__ENV__?.REACT_APP_GOOGLE_MAPS_API_KEY ||
  (window as typeof window & { REACT_APP_GOOGLE_MAPS_API_KEY?: string }).REACT_APP_GOOGLE_MAPS_API_KEY ||
  "";

const ensureRuntimeConfig = async () => {
  if (typeof window === "undefined") {
    return;
  }

  const currentConfig = (window as typeof window & { __APP_CONFIG__?: Record<string, string> }).__APP_CONFIG__;
  if (currentConfig?.REACT_APP_GOOGLE_MAPS_API_KEY) {
    return;
  }

  if (runtimeConfigPromise) {
    return runtimeConfigPromise;
  }

  runtimeConfigPromise = new Promise<void>((resolve, reject) => {
    const scriptId = "env-config-js-runtime";
    const existingScript = document.getElementById(scriptId);
    if (existingScript) {
      existingScript.remove();
    }

    const script = document.createElement("script");
    script.id = scriptId;
    script.async = true;
    script.defer = true;
    script.src = `/env-config.js?ts=${Date.now()}`;
    script.onload = () => {
      runtimeConfigPromise = null;
      resolve();
    };
    script.onerror = () => {
      runtimeConfigPromise = null;
      reject(new Error("Unable to load runtime configuration."));
    };
    document.head.appendChild(script);
  });

  return runtimeConfigPromise;
};

export const loadGoogleMapsScript = async () => {
  if (typeof window === "undefined") {
    throw new Error("Google Maps can only load in a browser.");
  }

  if ((window as typeof window & { google?: { maps?: unknown } }).google?.maps) {
    return;
  }

  if (googleMapsScriptPromise) {
    return googleMapsScriptPromise;
  }

  googleMapsScriptPromise = new Promise<void>((resolve, reject) => {
    void ensureRuntimeConfig()
      .catch(() => null)
      .finally(() => {
        const apiKey = getGoogleMapsApiKey();
        if (!apiKey) {
          googleMapsScriptPromise = null;
          reject(new Error("Google Maps API key is not configured on the frontend."));
          return;
        }

        const existingScript = document.getElementById("google-maps-js") as HTMLScriptElement | null;
        if (existingScript) {
          existingScript.addEventListener("load", () => resolve(), { once: true });
          existingScript.addEventListener(
            "error",
            () => {
              googleMapsScriptPromise = null;
              reject(new Error("Unable to load Google Maps right now."));
            },
            { once: true },
          );
          return;
        }

        const script = document.createElement("script");
        script.id = "google-maps-js";
        script.async = true;
        script.defer = true;
        script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}&v=weekly`;
        script.referrerPolicy = "origin";
        script.onload = () => resolve();
        script.onerror = () => {
          googleMapsScriptPromise = null;
          reject(new Error("Unable to load Google Maps right now."));
        };
        document.head.appendChild(script);
      });
  });

  return googleMapsScriptPromise;
};
